import { Alert, Button, EmptyState, Modal, Skeleton, parseDecimal } from '@catering/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FileSearch } from 'lucide-react';
import { type JSX, useState } from 'react';

import { formatPriceCents } from '../../../contracts/format-price';
import {
  allocatePayment,
  getAllocationSuggestion,
  type AllocationLineInput,
  type PaymentDetail,
} from '../../../payments/api';
import { mapPaymentError } from '../../../payments/payment-api-errors';

export interface AllocatePaymentModalProps {
  paymentId: string;
  /** `PaymentDetail.unallocatedCents`: the ceiling for the sum of every line. */
  unallocatedCents: number;
  onCancel: () => void;
  onAllocated: (detail: PaymentDetail) => void;
}

function centsToPesosString(cents: number): string {
  return (cents / 100).toFixed(2);
}

function parseAmountCents(pesos: string): number {
  const trimmed = pesos.trim();

  if (trimmed === '') {
    return 0;
  }

  const value = parseDecimal(trimmed);

  return value === null ? NaN : Math.round(value * 100);
}

/**
 * `payment-receipts/spec.md` §12: opens preloaded with the FIFO suggestion
 * (`GET /payments/:id/allocation-suggestion`), editable before confirming.
 * Same `Modal` shell as `HolidayFormModal`/`SuspendDialog`.
 */
export function AllocatePaymentModal({
  paymentId,
  unallocatedCents,
  onCancel,
  onAllocated,
}: AllocatePaymentModalProps): JSX.Element {
  // Edits the user typed, keyed by `invoiceId`: the suggestion's own amount
  // is the default for a line the user hasn't touched, computed straight
  // from `suggestionQuery.data` instead of copied into local state on load
  // (no `setState` in an effect just to seed a derived value).
  const [overrides, setOverrides] = useState<Record<string, string>>({});

  const suggestionQuery = useQuery({
    queryKey: ['payment-allocation-suggestion', paymentId],
    queryFn: () => getAllocationSuggestion(paymentId),
  });

  const mutation = useMutation({
    mutationFn: (allocations: AllocationLineInput[]) => allocatePayment(paymentId, allocations),
    onSuccess: (detail) => onAllocated(detail),
  });

  function updateAmount(invoiceId: string, amountPesos: string): void {
    setOverrides((current) => ({ ...current, [invoiceId]: amountPesos }));
  }

  const lines = suggestionQuery.data?.suggestions.map((suggestion) => ({
    invoiceId: suggestion.invoiceId,
    invoiceNumber: suggestion.invoiceNumber,
    dueDate: suggestion.dueDate,
    maxAmountCents: suggestion.amountCents,
    amountPesos: overrides[suggestion.invoiceId] ?? centsToPesosString(suggestion.amountCents),
  }));

  const parsedLines = (lines ?? []).map((line) => ({
    ...line,
    amountCents: parseAmountCents(line.amountPesos),
  }));

  const invalidAmount = parsedLines.some(
    (line) => Number.isNaN(line.amountCents) || line.amountCents < 0,
  );
  const totalCents = parsedLines.reduce((sum, line) => sum + Math.max(line.amountCents, 0), 0);
  const exceedsUnallocated = totalCents > unallocatedCents;
  const lineOverBalance = parsedLines.find((line) => line.amountCents > line.maxAmountCents);

  const positiveLines: AllocationLineInput[] = parsedLines
    .filter((line) => line.amountCents > 0)
    .map((line) => ({ invoiceId: line.invoiceId, amountCents: line.amountCents }));

  const canSubmit =
    lines !== undefined &&
    positiveLines.length > 0 &&
    !invalidAmount &&
    !exceedsUnallocated &&
    lineOverBalance === undefined;

  function handleSubmit(): void {
    if (!canSubmit) {
      return;
    }

    mutation.mutate(positiveLines);
  }

  return (
    <Modal open onClose={onCancel} title="Imputar a facturas" className="max-w-2xl">
      <div className="flex flex-col gap-4">
        {mutation.isError ? <Alert tone="danger">{mapPaymentError(mutation.error)}</Alert> : null}

        {suggestionQuery.isPending ? <Skeleton className="h-32 w-full" /> : null}

        {suggestionQuery.isError ? (
          <Alert tone="danger" title="No pudimos cargar la sugerencia de imputación">
            Ocurrió un error al cargar. Intentá de nuevo.
          </Alert>
        ) : null}

        {lines !== undefined && lines.length === 0 ? (
          <EmptyState icon={FileSearch} title="No hay facturas pendientes para imputar este recibo." />
        ) : null}

        {lines !== undefined && lines.length > 0 ? (
          <div className="flex flex-col gap-3">
            {lines.map((line) => {
              const parsed = parseAmountCents(line.amountPesos);
              const overBalance = !Number.isNaN(parsed) && parsed > line.maxAmountCents;

              return (
                <div
                  key={line.invoiceId}
                  className="grid grid-cols-1 gap-2 border-b border-slate-100 pb-3 last:border-b-0 sm:grid-cols-[1fr_1fr_1fr]"
                >
                  <span className="text-sm font-medium text-slate-900">{line.invoiceNumber}</span>
                  <span className="text-sm text-slate-500">
                    Saldo pendiente: {formatPriceCents(line.maxAmountCents)}
                  </span>
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor={`allocation-amount-${line.invoiceId}`}
                      className="sr-only"
                    >
                      Monto para la factura {line.invoiceNumber}
                    </label>
                    <input
                      id={`allocation-amount-${line.invoiceId}`}
                      inputMode="decimal"
                      value={line.amountPesos}
                      onChange={(event) => updateAmount(line.invoiceId, event.target.value)}
                      aria-invalid={overBalance}
                      className={`h-11 rounded-md border px-3 text-sm ${overBalance ? 'border-red-500' : 'border-slate-300'}`}
                    />
                  </div>
                </div>
              );
            })}

            <dl className="flex items-center justify-between border-t border-slate-200 pt-3 text-sm">
              <dt className="font-medium text-slate-900">Total a imputar</dt>
              <dd className="font-semibold text-slate-900">{formatPriceCents(totalCents)}</dd>
            </dl>

            {invalidAmount ? (
              <p role="alert" className="text-xs text-red-600">
                Revisá los montos: escribilos como 1.500,50 y sin signos.
              </p>
            ) : null}

            {exceedsUnallocated ? (
              <p role="alert" className="text-xs text-red-600">
                El total a imputar supera el monto disponible del recibo (
                {formatPriceCents(unallocatedCents)}).
              </p>
            ) : null}

            {lineOverBalance !== undefined ? (
              <p role="alert" className="text-xs text-red-600">
                El monto de la factura {lineOverBalance.invoiceNumber} supera el saldo pendiente de la
                factura.
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="flex items-center gap-3">
          <Button onClick={handleSubmit} disabled={!canSubmit} loading={mutation.isPending}>
            Confirmar imputación
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
